import {Router} from '@angular/router';
import {LoginResponse, OidcSecurityService} from 'angular-auth-oidc-client';
import {catchError, MonoTypeOperatorFunction, of, tap} from 'rxjs';

const RETRIED_KEY = 'ecoding.callbackLoginRetried';

/**
 * /callback: recover when checkAuth() throws, which otherwise stops the app from bootstrapping (blank page).
 *
 * The usual cause is "could not find matching config for state": the authorization response reached a tab that
 * did not start the login, e.g. Keycloak's "set new password" email link opened in a new tab, while the state is in
 * the other tab's sessionStorage. Keycloak has signed the user in by then, so a new login from this tab comes straight
 * back. Retried once per tab; a second failure in a row ends on /error/401 instead of a redirect loop.
 */
export function recoverFailedCallback(oidc: OidcSecurityService, router: Router): MonoTypeOperatorFunction<LoginResponse> {
  return source => source.pipe(
    tap(() => setRetried(false)),
    catchError(error => {
      if (wasRetried()) {
        setRetried(false);
        oidc.logoffLocal();
        void router.navigateByUrl('/error/401');
      } else {
        setRetried(true);
        oidc.authorize();
      }
      return of({isAuthenticated: false, userData: null, accessToken: '', idToken: '', errorMessage: String(error)});
    }),
  );
}

function wasRetried(): boolean {
  try {
    return sessionStorage.getItem(RETRIED_KEY) !== null;
  } catch {
    // Without storage the flag cannot stop a loop, so do not retry.
    return true;
  }
}

function setRetried(retried: boolean): void {
  try {
    if (retried) {
      sessionStorage.setItem(RETRIED_KEY, '1');
    } else {
      sessionStorage.removeItem(RETRIED_KEY);
    }
  } catch {
    // Storage unavailable: wasRetried() already reports true.
  }
}
